import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getPayments, createPayment, updatePaymentStatus } from '../services/api';

const PaymentsSection = ({ currentMember }) => {
  const [payments, setPayments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [notif, setNotif] = useState(null);
  const [formData, setFormData] = useState({
    type: 'mensalidade',
    amount: 0,
    description: '',
    payment_method: 'pix',
  });

  const showNotifMsg = (msg, type = 'info') => {
    setNotif({ msg, type });
    setTimeout(() => setNotif(null), 3000);
  };

  useEffect(() => {
    if (currentMember) {
      loadPayments();
    }
  }, [currentMember]);

  const loadPayments = async () => {
    if (!currentMember) return;
    try {
      setLoading(true);
      const response = await getPayments(currentMember.id);
      setPayments(response.data);
    } catch (error) {
      console.error('Erro ao carregar pagamentos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentMember) return;

    try {
      await createPayment({
        member_id: currentMember.id,
        member_name: currentMember.name,
        ...formData,
      });
      await loadPayments();
      setShowModal(false);
      setFormData({
        type: 'mensalidade',
        amount: 0,
        description: '',
        payment_method: 'pix',
      });
      showNotifMsg('Pagamento criado!', 'success');
    } catch (error) {
      console.error('Erro ao criar pagamento:', error);
      showNotifMsg('Erro ao criar pagamento', 'warn');
    }
  };

  const handlePaymentStatus = async (paymentId, status) => {
    try {
      await updatePaymentStatus(paymentId, {
        status,
        paid_at: status === 'pago' ? new Date().toISOString() : null,
      });
      await loadPayments();
      showNotifMsg('Status atualizado!', 'success');
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
    }
  };

  const filteredPayments = payments.filter((p) => filter === 'all' || p.status === filter);

  const stats = {
    total: payments.reduce((sum, p) => sum + p.amount, 0),
    pendente: payments.filter((p) => p.status === 'pendente').reduce((sum, p) => sum + p.amount, 0),
    pago: payments.filter((p) => p.status === 'pago').reduce((sum, p) => sum + p.amount, 0),
  };

  const getPaymentIcon = (method) => {
    switch(method) {
      case 'pix': return (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12.5 2.1l-1.4 1.4c-.4.4-.4 1 0 1.4l1.4 1.4-1.4 1.4c-.4.4-.4 1 0 1.4l1.4 1.4-1.4 1.4c-.4.4-.4 1 0 1.4l1.4 1.4-1.4 1.4c-.4.4-.4 1 0 1.4l1.4 1.4-1.4 1.4c-.4.4-.4 1 0 1.4l1.4 1.4"/>
        </svg>
      );
      case 'credito': return (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
        </svg>
      );
      case 'wcash': return (
        <span className="font-bold text-xs">WC$</span>
      );
      default: return (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
        </svg>
      );
    }
  };

  return (
    <div className="space-y-6">
      {/* Notification */}
      <AnimatePresence>
        {notif && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`px-4 py-2.5 rounded-xl text-xs font-['Jost'] ${
              notif.type === 'success' ? 'bg-green-500/10 border border-green-500/15 text-green-400/80' :
              notif.type === 'warn' ? 'bg-blue-500/10 border border-blue-500/20 text-blue-400/80' :
              'bg-white/5 border border-white/8 text-white/50'
            }`}
            data-testid="payments-notif"
          >
            {notif.msg}
          </motion.div>
        )}
      </AnimatePresence>

      {!currentMember ? (
        <div className="text-center py-20 bg-white/[0.02] border border-white/[0.06] rounded-2xl backdrop-blur-xl">
          <img src="/w-logo.png" alt="W" className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p className="text-white/40 text-sm">Selecione um membro na aba Cartoes</p>
        </div>
      ) : (
        <>
          {/* Header - Liquid Glass */}
          <div className="relative rounded-3xl overflow-hidden p-6 backdrop-blur-xl border border-white/[0.08]"
               style={{ background: 'linear-gradient(145deg, rgba(10,15,30,0.95) 0%, rgba(5,10,20,0.98) 100%)' }}>
            {/* Animated border */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none">
              <defs>
                <linearGradient id="payHeaderGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="rgba(255,255,255,0)" />
                  <stop offset="40%" stopColor="rgba(59,130,246,0.4)" />
                  <stop offset="50%" stopColor="rgba(255,255,255,0.6)" />
                  <stop offset="60%" stopColor="rgba(148,163,184,0.4)" />
                  <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                </linearGradient>
              </defs>
              <rect x="0" y="0" width="100%" height="100%" rx="24" fill="none" stroke="url(#payHeaderGrad)" strokeWidth="1" strokeDasharray="1200" className="animate-pay-border" />
            </svg>
            <style>{`
              @keyframes pay-border { 0% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: -2400; } }
              .animate-pay-border { animation: pay-border 10s linear infinite; }
            `}</style>
            
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 relative z-10">
              <div className="flex items-center gap-4">
                <img src="/w-logo.png" alt="W" className="w-12 h-12 object-contain" />
                <div>
                  <h2 className="text-2xl font-bold font-['Jost'] text-white/90">W Pay</h2>
                  <p className="text-white/40 text-sm">{currentMember.name}</p>
                </div>
              </div>
              <button
                onClick={() => setShowModal(true)}
                className="px-5 py-2 bg-blue-500/20 border border-blue-500/30 rounded-xl text-sm text-blue-400 hover:bg-blue-500/30 transition font-medium"
                data-testid="create-payment-button"
              >
                + Novo Pagamento
              </button>
            </div>
          </div>

          {/* Stats - Liquid Glass Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-2xl p-5 backdrop-blur-xl border border-white/[0.08]"
                 style={{ background: 'linear-gradient(145deg, rgba(15,20,35,0.9) 0%, rgba(10,15,25,0.95) 100%)' }}>
              <p className="text-white/40 text-xs font-['Jost'] uppercase tracking-wider">Total</p>
              <p className="text-2xl font-bold text-white/90 mt-2 font-['Jost']">R$ {stats.total.toFixed(2)}</p>
            </div>
            <div className="rounded-2xl p-5 backdrop-blur-xl border border-blue-400/20"
                 style={{ background: 'linear-gradient(145deg, rgba(59,130,246,0.08) 0%, rgba(15,20,35,0.9) 100%)' }}>
              <p className="text-blue-300/80 text-xs font-['Jost'] uppercase tracking-wider">Pendente</p>
              <p className="text-2xl font-bold text-blue-300 mt-2 font-['Jost']">R$ {stats.pendente.toFixed(2)}</p>
            </div>
            <div className="rounded-2xl p-5 backdrop-blur-xl border border-green-500/20"
                 style={{ background: 'linear-gradient(145deg, rgba(34,197,94,0.08) 0%, rgba(15,20,35,0.9) 100%)' }}>
              <p className="text-green-400/80 text-xs font-['Jost'] uppercase tracking-wider">Pago</p>
              <p className="text-2xl font-bold text-green-400 mt-2 font-['Jost']">R$ {stats.pago.toFixed(2)}</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-2">
            {['all', 'pendente', 'pago'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition ${
                  filter === f
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    : 'bg-white/[0.03] text-white/40 border border-white/[0.06] hover:bg-white/[0.06]'
                }`}
              >
                {f === 'all' ? 'Todos' : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>

          {/* Payments List */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="spinner"></div>
            </div>
          ) : filteredPayments.length === 0 ? (
            <div className="text-center py-20 bg-white/[0.02] border border-white/[0.06] rounded-2xl backdrop-blur-xl">
              <img src="/w-logo.png" alt="W" className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-white/40">Nenhum pagamento encontrado</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredPayments.map((payment) => (
                <motion.div
                  key={payment.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-2xl p-4 backdrop-blur-xl border border-white/[0.08] hover:border-blue-500/20 transition"
                  style={{ background: 'linear-gradient(145deg, rgba(15,20,35,0.9) 0%, rgba(10,15,25,0.95) 100%)' }}
                  data-testid={`payment-${payment.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        payment.payment_method === 'pix' ? 'bg-teal-500/15 text-teal-400' :
                        payment.payment_method === 'credito' ? 'bg-purple-500/15 text-purple-400' :
                        payment.payment_method === 'wcash' ? 'bg-blue-500/15 text-blue-400' :
                        'bg-blue-500/15 text-blue-400'
                      }`}>
                        {getPaymentIcon(payment.payment_method)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-white/90 font-['Jost']">{payment.description}</h3>
                        <p className="text-sm text-white/40">
                          {payment.type.charAt(0).toUpperCase() + payment.type.slice(1)} • {' '}
                          {new Date(payment.created_at).toLocaleDateString('pt-BR')}
                          {payment.payment_method && ` • ${payment.payment_method === 'pix' ? 'PIX' : payment.payment_method === 'credito' ? 'Credito' : payment.payment_method === 'wcash' ? 'Wcash' : 'Debito'}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-xl font-bold text-white/90 font-['Jost']">
                          R$ {payment.amount.toFixed(2)}
                        </p>
                        <p className={`text-xs font-semibold ${
                          payment.status === 'pago' ? 'text-green-400' :
                          payment.status === 'pendente' ? 'text-blue-300' : 'text-red-400'
                        }`}>
                          {payment.status.toUpperCase()}
                        </p>
                      </div>
                      {payment.status === 'pendente' && (
                        <button
                          onClick={() => handlePaymentStatus(payment.id, 'pago')}
                          className="px-4 py-2 bg-green-500/15 text-green-400 border border-green-500/25 rounded-xl text-sm hover:bg-green-500/25 transition"
                          data-testid={`pay-button-${payment.id}`}
                        >
                          Pago
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Contact WClub Info */}
          <div className="rounded-2xl p-5 backdrop-blur-xl border border-blue-500/20"
               style={{ background: 'linear-gradient(145deg, rgba(59,130,246,0.08) 0%, rgba(15,20,35,0.95) 100%)' }}>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
                </svg>
              </div>
              <div>
                <p className="text-white/80 font-['Jost'] font-medium">Precisa de ajuda?</p>
                <p className="text-white/40 text-sm">Entre em contato com o WClub para informacoes sobre pagamentos</p>
              </div>
            </div>
          </div>

          {/* Create Payment Modal */}
          <AnimatePresence>
            {showModal && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                onClick={() => setShowModal(false)}
              >
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  onClick={(e) => e.stopPropagation()}
                  className="rounded-2xl p-6 max-w-md w-full border border-white/[0.1] backdrop-blur-xl"
                  style={{ background: 'linear-gradient(145deg, rgba(15,20,35,0.98) 0%, rgba(10,15,25,0.99) 100%)' }}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <img src="/w-logo.png" alt="W" className="w-8 h-8" />
                    <h2 className="text-xl font-bold font-['Jost'] text-white/90">Novo Pagamento</h2>
                  </div>
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Type */}
                    <div>
                      <label className="text-[10px] text-white/40 uppercase tracking-wider mb-2 block">Tipo</label>
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white/90 outline-none focus:border-blue-500/50 transition"
                      >
                        <option value="mensalidade">Mensalidade</option>
                        <option value="evento">Evento</option>
                        <option value="produto">Produto</option>
                        <option value="servico">Servico</option>
                      </select>
                    </div>
                    
                    {/* Amount */}
                    <div>
                      <label className="text-[10px] text-white/40 uppercase tracking-wider mb-2 block">Valor (R$)</label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="0,00"
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                        required
                        className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white/90 placeholder:text-white/30 outline-none focus:border-blue-500/50 transition"
                        data-testid="payment-amount-input"
                      />
                    </div>
                    
                    {/* Description */}
                    <div>
                      <label className="text-[10px] text-white/40 uppercase tracking-wider mb-2 block">Descricao</label>
                      <input
                        type="text"
                        placeholder="Ex: Mensalidade Janeiro 2025"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        required
                        className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white/90 placeholder:text-white/30 outline-none focus:border-blue-500/50 transition"
                      />
                    </div>
                    
                    {/* Payment Method */}
                    <div>
                      <label className="text-[10px] text-white/40 uppercase tracking-wider mb-2 block">Forma de Pagamento</label>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { id: 'pix', label: 'PIX', color: 'teal' },
                          { id: 'credito', label: 'Credito', color: 'purple' },
                          { id: 'debito', label: 'Debito', color: 'blue' },
                        ].map((method) => (
                          <button
                            key={method.id}
                            type="button"
                            onClick={() => setFormData({ ...formData, payment_method: method.id })}
                            className={`p-3 rounded-xl border transition flex flex-col items-center gap-1 ${
                              formData.payment_method === method.id
                                ? `bg-${method.color}-500/15 border-${method.color}-500/30 text-${method.color}-400`
                                : 'bg-white/[0.03] border-white/[0.08] text-white/50 hover:border-white/[0.15]'
                            }`}
                            style={formData.payment_method === method.id ? {
                              backgroundColor: method.color === 'teal' ? 'rgba(20,184,166,0.15)' : method.color === 'purple' ? 'rgba(168,85,247,0.15)' : 'rgba(59,130,246,0.15)',
                              borderColor: method.color === 'teal' ? 'rgba(20,184,166,0.3)' : method.color === 'purple' ? 'rgba(168,85,247,0.3)' : 'rgba(59,130,246,0.3)',
                              color: method.color === 'teal' ? '#2dd4bf' : method.color === 'purple' ? '#a855f7' : '#60a5fa'
                            } : {}}
                          >
                            {getPaymentIcon(method.id)}
                            <span className="text-xs">{method.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex gap-3 mt-6">
                      <button
                        type="submit"
                        className="flex-1 px-6 py-3 bg-blue-500/20 border border-blue-500/30 text-blue-400 font-semibold rounded-xl hover:bg-blue-500/30 transition"
                      >
                        Criar Pagamento
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowModal(false)}
                        className="px-6 py-3 bg-white/[0.04] border border-white/[0.08] rounded-xl text-white/50 hover:bg-white/[0.08] transition"
                      >
                        Cancelar
                      </button>
                    </div>
                  </form>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </div>
  );
};

export default PaymentsSection;
